import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Layout, message, ConfigProvider } from 'antd';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import SideMenu from './components/SideMenu';
import Dashboard from './pages/Dashboard';
import WhatsAppWorkflowPage from './pages/WhatsAppWorkflowPage';
import WorkflowListPage from './pages/WorkflowListPage';
import WorkflowMonitorPage from './pages/WorkflowMonitorPage';
import ChatBotInboxPage from './pages/ChatBotInboxPage';
import MyPreferencesModal from './components/MyPreferencesModal';
import CompanyUserAdminPage from './pages/CompanyUserAdminPage';
import CompanyEditPage from './pages/CompanyEditPage';
import WhatsAppTemplateList from './pages/WhatsAppTemplateList';

import EFormListPage from './pages/EFormListPage';
import EFormInstancePage from './pages/EFormInstancePage';
import ReportInstancePage from './pages/ReportInstancePage';
import DocInstancePage from './pages/DocInstancePage';
import StudioTest from './pages/StudioTest';
import PublishedAppsPage from './pages/PublishedAppsPage';
import PendingTasksPage from './pages/PendingTasksPage';
import './App.css';
import { syncWpaManifestHead } from './utils/wpaManifestHead';
import DataSetManagementPage from './pages/DataSetManagementPage';
import ContactListPage from './pages/ContactListPage';
import ContactEditPage from './pages/ContactEditPage';
import ContactImportPage from './pages/ContactImportPage';
import ContactImportSchedulePage from './pages/ContactImportSchedulePage';
import BroadcastGroupsPage from './pages/BroadcastGroupsPage';
import HashtagsPage from './pages/HashtagsPage';
import OnboardingTrigger from './components/OnboardingTrigger';
import OnboardingManagementPage from './pages/OnboardingManagementPage';
import OnboardingTest from './components/OnboardingTest';
import PhoneVerificationPage from './pages/PhoneVerificationPage';
import OnboardingWizardPage from './pages/OnboardingWizardPage';
import CompanyPhoneVerificationAdminPage from './pages/CompanyPhoneVerificationAdminPage';
import PermissionManagementPage from './pages/PermissionManagementPage';
import ApiProviderManagementPage from './pages/ApiProviderManagementPage';
import ReportsPage from './pages/Reports/ReportsPage';
import CommerceCollectionsPage from './pages/CommerceCollectionsPage';
import CommerceProductsPage from './pages/CommerceProductsPage';
import CommerceImportPage from './pages/CommerceImportPage';
import CommerceImportSchedulePage from './pages/CommerceImportSchedulePage';

import CommerceCatalogAdminPage from './pages/CommerceCatalogAdminPage';
import CommerceCustomersPage from './pages/CommerceCustomersPage';
import CommercePriceCardsPage from './pages/CommercePriceCardsPage';
import CommerceSalesOrdersPage from './pages/CommerceSalesOrdersPage';
import CommerceWpaPermissionPage from './pages/CommerceWpaPermissionPage';
import CommerceDeliveryRulesPage from './pages/CommerceDeliveryRulesPage';
import CommerceWpaHomePromotionPage from './pages/CommerceWpaHomePromotionPage';
import AiEmployeeProfilesPage from './pages/AiEmployeeProfilesPage';
import WorkflowPackagePage from './pages/WorkflowPackagePage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ReportTemplatesPage from './pages/Studio/ReportTemplates/ReportTemplatesPage';
import BuildAppPage from './pages/Studio/BuildApp/BuildAppPage';
import BuildAppWizardPage from './pages/Studio/BuildApp/BuildAppWizardPage';
import TemplateReportBuilderPage from './pages/Reports/TemplateReportBuilder/TemplateReportBuilderPage';
import CalendarPage from './pages/CalendarPage';
import DocumentLibraryPage from './pages/DocumentLibraryPage';
import UsageBillingSettingsPage from './pages/UsageBillingSettingsPage';
import WpaLoginPage from './pages/wpa/WpaLoginPage';
import WpaResetPasswordPage from './pages/wpa/WpaResetPasswordPage';
import WpaHomePage from './pages/wpa/WpaHomePage';
import WpaCategoriesPage from './pages/wpa/WpaCategoriesPage';
import WpaProductListPage from './pages/wpa/WpaProductListPage';
import WpaCartDocsPage from './pages/wpa/WpaCartDocsPage';
import WpaCartDetailPage from './pages/wpa/WpaCartDetailPage';
import WpaOrderDetailPage from './pages/wpa/WpaOrderDetailPage';
import WpaOrdersPage from './pages/wpa/WpaOrdersPage';
import WpaAccountPage from './pages/wpa/WpaAccountPage';
import WpaChangePasswordPage from './pages/wpa/WpaChangePasswordPage';
import WpaCommerceShell from './pages/wpa/WpaCommerceShell';
import { expandInterfacesWithChildren } from './utils/permissionUtils';

const { Content } = Layout;

/** 依 interface 權限保護路由；權限尚未載入時不阻擋，載入後無權則導向 dashboard */
const RequirePermission = ({ interfaceKey, children }) => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
    const interfaces = userInfo?.interfaces || [];
    const expanded = expandInterfacesWithChildren(interfaces);
    if (expanded.length > 0 && !expanded.includes(interfaceKey)) {
      return <Navigate to="/dashboard" replace />;
    }
  } catch {
    /* 解析失敗時放行，由頁面內檢查 */
  }
  return children;
};

const pathToMenuKey = {
  '/dashboard': 'dashboard',
  '/calendar': 'calendar',
  '/document-library': 'documentLibrary',
  '/pending-tasks': 'pendingTasks',
  '/whatsapp-workflow': 'whatsappWorkflow',
  '/workflow-list': 'whatsappWorkflow',
  '/workflow-monitor': 'workflowMonitor',
  '/chatbot-inbox': 'chatBotInbox',
  '/whatsapp-templates': 'whatsappTemplates',
  '/eform-list': 'eformList',
  '/company-user-admin': 'companyUserAdmin',
  '/data-sets': 'dataSets',
  '/published-apps': 'publishedApps',
  '/contacts': 'contactList',
  '/contacts/new': 'contactList',
  '/contacts/edit/:id': 'contactList',
  '/contacts/import': 'contactImport',
  '/contact-import-schedule': 'contactImportSchedule',
  '/broadcast-groups': 'broadcastGroups',
  '/hashtags': 'hashtags',
  '/onboarding': 'onboarding',
  '/onboarding-test': 'onboarding-test',
  '/phone-verification-admin': 'phoneVerificationAdmin',
  '/permission-management': 'permissionManagement',
  '/api-providers': 'apiProviders',
  '/reports/daily': 'reports.daily',
  '/reports/monthly': 'reports.monthly',
  '/reports/realtime': 'reports.realtime',
  '/reports/apps-media': 'reports.appsMedia',
  '/reports/eform-instances': 'reports.eformInstances',
  '/reports/eform-pivot': 'reports.eformPivot',
  '/reports/commerce': 'reports.commerce',
  '/reports/billing-system': 'reports.billingSystem',
  '/admin/usage-billing-settings': 'admin.usageBilling.settings',
  '/commerce/collections': 'commerce.collections',
  '/commerce/products': 'commerce.products',
  '/commerce/wpa-home-promotions': 'commerce.wpaHomePromotions',
  '/commerce/import': 'commerce.import',
  '/commerce/import-schedule': 'commerce.import',
  '/commerce/preview': 'commerce.preview',
  '/commerce/catalog-admin': 'commerce.catalogAdmin',
  '/commerce/customers': 'commerce.customers',
  '/commerce/price-cards': 'commerce.priceCards',
  '/commerce/sales-orders': 'commerce.salesOrders',
  '/commerce/wpa-permissions': 'commerce.wpaPermissions',
  '/commerce/delivery-rules': 'commerce.deliveryRules',
  '/ai-employee-profiles': 'aiEmployeeProfiles',
  '/studio/build-app': 'buildApp',
  '/studio/build-app/wizard': 'buildApp',
  '/studio/workflow-package': 'workflowPackage',
  '/studio/knowledge-base': 'knowledgeBase',
  '/studio/report-templates': 'reportTemplates',
  '/reports/template-report': 'reports.templateReport',
};

function MainLayout({ userInfo, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMenuKey, setSelectedMenuKey] = useState('dashboard');

  useEffect(() => {
    const key = pathToMenuKey[location.pathname] || 'dashboard';
    setSelectedMenuKey(key);
  }, [location.pathname]);

  const handleMenuSelect = (key) => {
    setSelectedMenuKey(key);
    switch (key) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pendingTasks':
        navigate('/pending-tasks');
        break;
      case 'calendar':
        navigate('/calendar');
        break;
      case 'documentLibrary':
        navigate('/document-library');
        break;
      case 'whatsappWorkflow':
        navigate('/workflow-list');
        break;
      case 'workflowMonitor':
        navigate('/workflow-monitor');
        break;
      case 'chatBotInbox':
        navigate('/chatbot-inbox');
        break;
      case 'whatsappTemplates':
        navigate('/whatsapp-templates');
        break;
      case 'eformList':
        navigate('/eform-list');
        break;
      case 'companyUserAdmin':
        navigate('/company-user-admin');
        break;
      case 'dataSets':
        navigate('/data-sets');
        break;
      case 'publishedApps':
        navigate('/published-apps');
        break;
      case 'contactList':
        navigate('/contacts');
        break;
      case 'broadcastGroups':
        navigate('/broadcast-groups');
        break;
      case 'hashtags':
        navigate('/hashtags');
        break;
      case 'phoneVerificationAdmin':
        navigate('/phone-verification-admin');
        break;
      case 'permissionManagement':
        navigate('/permission-management');
        break;
      case 'apiProviders':
        navigate('/api-providers');
        break;
      case 'aiEmployeeProfiles':
        navigate('/ai-employee-profiles');
        break;
      case 'buildApp':
        navigate('/studio/build-app');
        break;
      case 'workflowPackage':
        navigate('/studio/workflow-package');
        break;
      case 'knowledgeBase':
        navigate('/studio/knowledge-base');
        break;
      case 'reportTemplates':
        navigate('/studio/report-templates');
        break;
      case 'reports.templateReport':
        navigate('/reports/template-report');
        break;
      case 'reports.daily':
        navigate('/reports/daily');
        break;
      case 'reports.monthly':
        navigate('/reports/monthly');
        break;
      case 'reports.realtime':
        navigate('/reports/realtime');
        break;
      case 'reports.appsMedia':
        navigate('/reports/apps-media');
        break;
      case 'reports.eformInstances':
        navigate('/reports/eform-instances');
        break;
      case 'reports.eformPivot':
        navigate('/reports/eform-pivot');
        break;
      case 'reports.commerce':
        navigate('/reports/commerce');
        break;
      case 'reports.billingSystem':
        navigate('/reports/billing-system');
        break;
      case 'admin.usageBilling.settings':
        navigate('/admin/usage-billing-settings');
        break;
      case 'commerce.collections':
        navigate('/commerce/collections');
        break;
      case 'commerce.products':
        navigate('/commerce/products');
        break;
      case 'commerce.wpaHomePromotions':
        navigate('/commerce/wpa-home-promotions');
        break;
      case 'commerce.import':
        navigate('/commerce/import');
        break;
      case 'commerce.preview':
        navigate('/commerce/preview');
        break;
      case 'commerce.catalogAdmin':
        navigate('/commerce/catalog-admin');
        break;
      case 'commerce.customers':
        navigate('/commerce/customers');
        break;
      case 'commerce.priceCards':
        navigate('/commerce/price-cards');
        break;
      case 'commerce.salesOrders':
        navigate('/commerce/sales-orders');
        break;
      default:
        navigate('/dashboard');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <SideMenu
        userInfo={userInfo}
        onLogout={onLogout}
        onMenuSelect={handleMenuSelect}
        selectedKey={selectedMenuKey}
      />
      <Layout className="main-content-layout">
        <Content className="main-content-panel">
          <Routes>
            <Route path="/dashboard" element={<Dashboard onMenuSelect={handleMenuSelect} />} />
            <Route path="/whatsapp-workflow" element={<WhatsAppWorkflowPage />} />
            <Route path="/workflow-list" element={<WorkflowListPage />} />
            <Route path="/workflow-monitor" element={<WorkflowMonitorPage />} />
            <Route path="/chatbot-inbox" element={<ChatBotInboxPage />} />
            <Route path="/company-user-admin" element={<CompanyUserAdminPage />} />
            <Route path="/company-edit" element={<CompanyEditPage />} />
            <Route path="/eform-list" element={<EFormListPage />} />
            <Route path="/eform-instance/:id" element={<EFormInstancePage />} />
            <Route path="/report-instance/:id" element={<ReportInstancePage />} />
            <Route path="/doc-instance/:id" element={<DocInstancePage />} />
            <Route path="/whatsapp-templates" element={<WhatsAppTemplateList />} />
            <Route path="/studio-test" element={<StudioTest />} />
            <Route path="/data-sets" element={<DataSetManagementPage />} />
            <Route path="/ai-employee-profiles" element={<AiEmployeeProfilesPage />} />
            <Route path="/studio/workflow-package" element={<WorkflowPackagePage />} />
            <Route path="/studio/knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="/published-apps" element={<PublishedAppsPage />} />
            <Route path="/pending-tasks" element={<PendingTasksPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/document-library" element={<DocumentLibraryPage />} />
            <Route path="/contacts" element={<ContactListPage />} />
            <Route path="/contacts/new" element={<ContactEditPage />} />
            <Route path="/contacts/edit/:id" element={<ContactEditPage />} />
            <Route path="/contacts/import" element={<ContactImportPage />} />
            <Route path="/contact-import-schedule" element={<ContactImportSchedulePage />} />
            <Route path="/broadcast-groups" element={<BroadcastGroupsPage />} />
            <Route path="/hashtags" element={<HashtagsPage />} />
            <Route path="/phone-verification-admin" element={<CompanyPhoneVerificationAdminPage />} />
              <Route path="/permission-management" element={<PermissionManagementPage />} />
              <Route path="/api-providers" element={<ApiProviderManagementPage />} />
              <Route path="/reports/daily" element={<ReportsPage />} />
              <Route path="/reports/monthly" element={<ReportsPage />} />
              <Route path="/reports/realtime" element={<ReportsPage />} />
              <Route path="/reports/apps-media" element={<ReportsPage />} />
            <Route path="/reports/eform-instances" element={<ReportsPage />} />
            <Route path="/reports/eform-pivot" element={<ReportsPage />} />
            <Route path="/reports/commerce" element={<ReportsPage />} />
            <Route path="/commerce/collections" element={<CommerceCollectionsPage />} />
            <Route path="/commerce/products" element={<CommerceProductsPage />} />
            <Route path="/commerce/wpa-home-promotions" element={<CommerceWpaHomePromotionPage />} />
            <Route path="/commerce/import" element={<CommerceImportPage />} />
            <Route path="/commerce/import-schedule" element={<CommerceImportSchedulePage />} />
              <Route path="/commerce/catalog-admin" element={<CommerceCatalogAdminPage />} />
              <Route path="/commerce/customers" element={<CommerceCustomersPage />} />
              <Route path="/commerce/price-cards" element={<CommercePriceCardsPage />} />
              <Route path="/commerce/sales-orders" element={<CommerceSalesOrdersPage />} />
              <Route path="/commerce/delivery-rules" element={<CommerceDeliveryRulesPage />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
}

function AppContent() {
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [systemVersion, setSystemVersion] = useState('');
  const [showPreferences, setShowPreferences] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMenuKey, setSelectedMenuKey] = useState('dashboard');

  // adminLoginPath：從 localStorage 快取初始化，null 表示尚未載入過（第一次訪問）
  const [loginPath, setLoginPath] = useState(() => localStorage.getItem('adminLoginPath'));
  // 有效登入路徑：空字串或 null 時使用預設 '/'
  const effectiveLoginPath = loginPath || '/';

  useLayoutEffect(() => {
    syncWpaManifestHead(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const key = pathToMenuKey[location.pathname];
    if (key) setSelectedMenuKey(key);
  }, [location.pathname]);

  // 匯入相關 API 逾時：依後端 appsettings ImportSql:ClientHttpTimeoutMs
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { commerceImportHttp } = await import('./services/commerceImportApi');
        const { contactImportHttp } = await import('./services/contactImportApi');
        const { initializeImportSqlHttpTimeouts } = await import('./services/importSqlClientConfig');
        if (!cancelled) await initializeImportSqlHttpTimeouts([commerceImportHttp, contactImportHttp]);
      } catch (e) {
        console.warn('Import SQL HTTP timeout init failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // 檢查是否是表單實例頁面（允許匿名訪問）
  const isEFormInstancePage = location.pathname.startsWith('/eform-instance/');
  const isArtifactPreviewPage = location.pathname.startsWith('/preview/report-instance/') ||
    location.pathname.startsWith('/preview/doc-instance/');
  const isPhoneVerificationPage = location.pathname.startsWith('/phone-verification/');
  const isOnboardingWizardPage = location.pathname.startsWith('/onboarding-wizard/');
  const isWpaPage = location.pathname.includes('/wpa/');

  const handleMenuSelect = (key) => {
    setSelectedMenuKey(key);
    switch (key) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pendingTasks':
        navigate('/pending-tasks');
        break;
      case 'calendar':
        navigate('/calendar');
        break;
      case 'documentLibrary':
        navigate('/document-library');
        break;
      case 'whatsappWorkflow':
        navigate('/workflow-list');
        break;
      case 'workflowMonitor':
        navigate('/workflow-monitor');
        break;
      case 'chatBotInbox':
        navigate('/chatbot-inbox');
        break;
      case 'companyUserAdmin':
        navigate('/company-user-admin');
        break;
      case 'eformList':
        navigate('/eform-list');
        break;
      case 'whatsappTemplates':
        navigate('/whatsapp-templates');
        break;
      case 'dataSets':
        navigate('/data-sets');
        break;
      case 'aiEmployeeProfiles':
        navigate('/ai-employee-profiles');
        break;
      case 'buildApp':
        navigate('/studio/build-app');
        break;
      case 'workflowPackage':
        navigate('/studio/workflow-package');
        break;
      case 'knowledgeBase':
        navigate('/studio/knowledge-base');
        break;
      case 'reportTemplates':
        navigate('/studio/report-templates');
        break;
      case 'reports.templateReport':
        navigate('/reports/template-report');
        break;
      case 'publishedApps':
        navigate('/published-apps');
        break;
      case 'contactList':
        navigate('/contacts');
        break;
      case 'contactImport':
        navigate('/contacts/import');
        break;
      case 'contactImportSchedule':
        navigate('/contact-import-schedule');
        break;
      case 'broadcastGroups':
        navigate('/broadcast-groups');
        break;
      case 'hashtags':
        navigate('/hashtags');
        break;
      case 'phoneVerificationAdmin':
        navigate('/phone-verification-admin');
        break;
      case 'permissionManagement':
        navigate('/permission-management');
        break;
      case 'apiProviders':
        navigate('/api-providers');
        break;
      case 'reports.daily':
        navigate('/reports/daily');
        break;
      case 'reports.monthly':
        navigate('/reports/monthly');
        break;
      case 'reports.realtime':
        navigate('/reports/realtime');
        break;
      case 'reports.appsMedia':
        navigate('/reports/apps-media');
        break;
      case 'reports.eformInstances':
        navigate('/reports/eform-instances');
        break;
      case 'reports.eformPivot':
        navigate('/reports/eform-pivot');
        break;
      case 'reports.commerce':
        navigate('/reports/commerce');
        break;
      case 'reports.billingSystem':
        navigate('/reports/billing-system');
        break;
      case 'admin.usageBilling.settings':
        navigate('/admin/usage-billing-settings');
        break;
      case 'commerce.collections':
        navigate('/commerce/collections');
        break;
      case 'commerce.products':
        navigate('/commerce/products');
        break;
      case 'commerce.wpaHomePromotions':
        navigate('/commerce/wpa-home-promotions');
        break;
      case 'commerce.import':
        navigate('/commerce/import');
        break;
      case 'commerce.preview':
        navigate('/commerce/preview');
        break;
      case 'commerce.catalogAdmin':
        navigate('/commerce/catalog-admin');
        break;
      case 'commerce.customers':
        navigate('/commerce/customers');
        break;
      case 'commerce.priceCards':
        navigate('/commerce/price-cards');
        break;
      case 'commerce.salesOrders':
        navigate('/commerce/sales-orders');
        break;
      case 'commerce.wpaPermissions':
        navigate('/commerce/wpa-permissions');
        break;
      case 'commerce.deliveryRules':
        navigate('/commerce/delivery-rules');
        break;
      case 'onboarding':
        navigate('/onboarding');
        break;
      case 'onboarding-test':
        navigate('/onboarding-test');
        break;
      default:
        navigate('/dashboard');
    }
  };

  // 初始化時檢查 localStorage
  useEffect(() => {
    const savedUser = localStorage.getItem('userInfo');
    if (savedUser) {
      setIsLoggedIn(true);
      setUserInfo(JSON.parse(savedUser));
    }
  }, []);

  // 載入平台登入路徑設定（AdminLoginPath），並存入 localStorage 供其他服務使用
  useEffect(() => {
    fetch('/api/auth/login-config', { cache: 'no-store' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        const path = (data?.loginPath ?? '').trim();
        localStorage.setItem('adminLoginPath', path);
        setLoginPath(path);
      })
      .catch(() => {
        // 網路失敗時保留快取值，不覆寫
      });
  }, []);

  useEffect(() => {
    if (isLoggedIn || systemVersion) {
      return;
    }

    let isMounted = true;

    fetch('/api/system/version', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (!isMounted || !data?.version) {
          return;
        }
        const rawVersion = typeof data.version === 'string' ? data.version.trim() : '';
        if (rawVersion) {
          setSystemVersion(rawVersion);
        }
      })
      .catch(() => {
        // 靜默失敗，登入頁不顯示版本
      });

    return () => {
      isMounted = false;
    };
  }, [isLoggedIn, systemVersion]);

  // 處理登入後的重定向（含 onboarding 強制導覽 + 公司被刪除檢測）
  useEffect(() => {
    if (!isLoggedIn) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // 每次路由變化時驗證用戶/公司是否仍有效
    const verifySession = async () => {
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          if (data.error === 'company_deleted') {
            message.error(data.message || '公司帳號已被移除，請重新登入');
          } else if (data.error === 'account_disabled') {
            message.error(data.message || '您的帳號已被停用，請聯繫管理員。');
          }
          setIsLoggedIn(false);
          setUserInfo(null);
          localStorage.removeItem('userInfo');
          localStorage.removeItem('token');
          sessionStorage.clear();
          navigate(effectiveLoginPath);
          return;
        }
        if (res.ok) {
          const userData = await res.json();
          // 必須用函數式更新：此 fetch 完成時 effect 閉包裡的 userInfo 可能仍為 null
          //（登入流程尚未 setUserInfo），若用 ...userInfo 會變成只剩 needs_onboarding / company_id，
          // 覆寫掉稍後寫入的完整資料，側邊欄會一直顯示預設「User」。
          setUserInfo((prev) => {
            if (!prev) {
              return prev;
            }
            const updatedInfo = {
              ...prev,
              needs_onboarding: userData.needs_onboarding || false,
              company_id: userData.company_id || prev.company_id,
            };
            if (JSON.stringify(updatedInfo) !== JSON.stringify(prev)) {
              localStorage.setItem('userInfo', JSON.stringify(updatedInfo));
              return updatedInfo;
            }
            return prev;
          });
        }
      } catch { /* network error, ignore */ }
    };

    const isOnWizard = location.pathname.startsWith('/onboarding-wizard/');

    if (userInfo?.needs_onboarding && userInfo?.company_id && !isOnWizard) {
      navigate(`/onboarding-wizard/${userInfo.company_id}`);
      return;
    }

    if (location.pathname === effectiveLoginPath) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectPath);
      } else {
        navigate('/dashboard');
      }
    }

    verifySession();
  }, [isLoggedIn, location.pathname, navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: values.email, password: values.password })
      });
      const data = await response.json();
      if (data.success) {
        message.success(t('login.loginSuccess'));
        
        // 先設置基本的登入狀態
        setIsLoggedIn(true);
        localStorage.setItem('token', data.token);
        
        // 檢查是否有重定向目標
        const redirectPath = sessionStorage.getItem('redirectAfterLogin');
        console.log('登入成功，檢查重定向目標:', redirectPath);
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          console.log('準備重定向到:', redirectPath);
          // 使用 setTimeout 確保狀態更新完成後再重定向
          setTimeout(() => {
            console.log('執行重定向到:', redirectPath);
            navigate(redirectPath);
          }, 100);
        } else {
          console.log('沒有重定向目標，將導航到默認頁面');
        }
        
        // 使用 token 調用 /api/auth/me 獲取完整的用戶信息
        try {
          const meResponse = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${data.token}`
            }
          });
          
          if (meResponse.ok) {
            const userData = await meResponse.json();
            const fullUserInfo = {
              user_id: userData.user_id,
              account: userData.account,
              name: userData.name,
              email: userData.email,
              phone: userData.phone,
              language: userData.language,
              timezone: userData.timezone,
              avatar_url: userData.avatar_url,
              company_id: userData.company_id || data.user.companyId,
              roles: userData.roles || [],
              needs_onboarding: userData.needs_onboarding || false
            };
            
            setUserInfo(fullUserInfo);
            localStorage.setItem('userInfo', JSON.stringify(fullUserInfo));
            console.log('[Login] userInfo 已設定 (auth/me), company_id:', fullUserInfo.company_id, 'needs_onboarding:', fullUserInfo.needs_onboarding);

            if (fullUserInfo.needs_onboarding && fullUserInfo.company_id) {
              console.log('[Login] 需要 onboarding，跳轉到 /onboarding-wizard/', fullUserInfo.company_id);
              setTimeout(() => {
                navigate(`/onboarding-wizard/${fullUserInfo.company_id}`);
              }, 100);
              return;
            }
          } else {
            const meErrorData = await meResponse.json().catch(() => ({}));
            if (meResponse.status === 401 && meErrorData.error === 'account_disabled') {
              message.error(meErrorData.message || '您的帳號已被停用，請聯繫管理員。');
              setIsLoggedIn(false);
              localStorage.removeItem('token');
              localStorage.removeItem('userInfo');
              return;
            }
            // 如果 /api/auth/me 失敗，使用登入響應中的基本信息
            const basicUserInfo = {
              user_id: data.user.id,
              account: data.user.account,
              name: data.user.name,
              email: data.user.email,
              phone: '',
              language: 'zh-TC',
              timezone: 'Asia/Hong_Kong',
              avatar_url: '',
              company_id: data.user.companyId
            };
            
            setUserInfo(basicUserInfo);
            localStorage.setItem('userInfo', JSON.stringify(basicUserInfo));
            console.log('[Login] userInfo 已設定 (auth/me 失敗 fallback), company_id:', basicUserInfo.company_id);
          }
        } catch (meError) {
          // 如果調用 /api/auth/me 失敗，使用登入響應中的基本信息
          const basicUserInfo = {
            user_id: data.user.id,
            account: data.user.account,
            name: data.user.name,
            email: data.user.email,
            phone: '',
            language: 'zh-TC',
            timezone: 'Asia/Hong_Kong',
            avatar_url: '',
            company_id: data.user.companyId
          };
          
          setUserInfo(basicUserInfo);
          localStorage.setItem('userInfo', JSON.stringify(basicUserInfo));
          console.log('[Login] userInfo 已設定 (auth/me 例外 fallback), company_id:', basicUserInfo.company_id);
        }
      } else {
        message.error(data.message || t('login.loginFailed'));
      }
    } catch (error) {
      message.error(t('login.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    // 呼叫後端 logout 以清除 HttpOnly Cookie（/Uploads 權限用）
    fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    }).catch(() => {});
    setIsLoggedIn(false);
    setUserInfo(null);
    localStorage.removeItem('userInfo');
    localStorage.removeItem('token');
    sessionStorage.clear();
    navigate(effectiveLoginPath);
    message.success(t('login.logoutSuccess'));
  };

  // Workflow Monitor 內嵌預覽用：保留原本正式 URL，另提供不帶 SideMenu 的內容 URL。
  if (isArtifactPreviewPage) {
    return (
      <Routes>
        <Route path="/preview/report-instance/:id" element={<ReportInstancePage />} />
        <Route path="/preview/doc-instance/:id" element={<DocInstancePage />} />
      </Routes>
    );
  }

  // 如果是表單實例頁面或電話驗證頁面，允許匿名訪問（不需要登入）
  if (isEFormInstancePage || isPhoneVerificationPage || isOnboardingWizardPage) {
    return (
      <Routes>
        <Route path="/eform-instance/:id" element={<EFormInstancePage />} />
        <Route path="/phone-verification/:verificationId" element={<PhoneVerificationPage />} />
        <Route path="/onboarding-wizard/:companyId" element={<OnboardingWizardPage />} />
      </Routes>
    );
  }

  if (isWpaPage) {
    return (
      <Routes>
        <Route path="/:companyId/wpa/login" element={<WpaLoginPage />} />
        <Route path="/:companyId/wpa/reset-password" element={<WpaResetPasswordPage />} />
        <Route path="/:companyId/wpa" element={<WpaCommerceShell />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<WpaHomePage />} />
          <Route path="categories" element={<WpaCategoriesPage />} />
          <Route path="products" element={<WpaProductListPage />} />
          <Route path="cart" element={<WpaCartDocsPage />} />
          <Route path="cart/:cartId" element={<WpaCartDetailPage />} />
          <Route path="orders" element={<WpaOrdersPage />} />
          <Route path="orders/:orderId" element={<WpaOrderDetailPage />} />
          <Route path="account" element={<WpaAccountPage />} />
          <Route path="change-password" element={<WpaChangePasswordPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  if (isLoggedIn) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <SideMenu
          key={userInfo?.user_id || 'guest'}
          userInfo={userInfo}
          onLogout={handleLogout}
          onMenuSelect={handleMenuSelect}
          selectedKey={selectedMenuKey}
          onAvatarClick={() => setShowPreferences(true)}
        />
        <Layout className="main-content-layout">
          <Content className="main-content-panel">
            <Routes>
              <Route path="/dashboard" element={<Dashboard onMenuSelect={handleMenuSelect} />} />
              <Route path="/whatsapp-workflow" element={<WhatsAppWorkflowPage />} />
              <Route path="/workflow-list" element={<WorkflowListPage />} />
              <Route path="/workflow-monitor" element={<WorkflowMonitorPage />} />
              <Route path="/chatbot-inbox" element={<ChatBotInboxPage />} />
              <Route path="/company-user-admin" element={
                <RequirePermission interfaceKey="companyUserAdmin">
                  <CompanyUserAdminPage />
                </RequirePermission>
              } />
              <Route path="/company-edit" element={<CompanyEditPage />} />
              <Route path="/eform-list" element={<EFormListPage />} />
              <Route path="/eform-instance/:id" element={<EFormInstancePage />} />
              <Route path="/report-instance/:id" element={<ReportInstancePage />} />
              <Route path="/doc-instance/:id" element={<DocInstancePage />} />
              <Route path="/whatsapp-templates" element={<WhatsAppTemplateList />} />
              <Route path="/studio-test" element={<StudioTest />} />
              <Route path="/data-sets" element={<DataSetManagementPage />} />
              <Route path="/ai-employee-profiles" element={<AiEmployeeProfilesPage />} />
              <Route path="/studio/build-app/wizard" element={<BuildAppWizardPage />} />
              <Route path="/studio/build-app" element={<BuildAppPage />} />
              <Route path="/studio/workflow-package" element={<WorkflowPackagePage />} />
              <Route path="/studio/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/studio/report-templates" element={<ReportTemplatesPage />} />
              <Route path="/reports/template-report" element={<TemplateReportBuilderPage />} />
              <Route path="/published-apps" element={<PublishedAppsPage />} />
              <Route path="/pending-tasks" element={<PendingTasksPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/document-library" element={<DocumentLibraryPage />} />
              <Route path="/contacts" element={<ContactListPage />} />
              <Route path="/contacts/new" element={<ContactEditPage />} />
              <Route path="/contacts/edit/:id" element={<ContactEditPage />} />
              <Route path="/contacts/import" element={<ContactImportPage />} />
              <Route path="/contact-import-schedule" element={<ContactImportSchedulePage />} />
              <Route path="/broadcast-groups" element={<BroadcastGroupsPage />} />
              <Route path="/hashtags" element={<HashtagsPage />} />
              <Route path="/phone-verification-admin" element={
                <RequirePermission interfaceKey="phoneVerificationAdmin">
                  <CompanyPhoneVerificationAdminPage />
                </RequirePermission>
              } />
              <Route path="/permission-management" element={
                <RequirePermission interfaceKey="permissionManagement">
                  <PermissionManagementPage />
                </RequirePermission>
              } />
              <Route path="/api-providers" element={<ApiProviderManagementPage />} />
              <Route path="/reports/daily" element={<ReportsPage />} />
              <Route path="/reports/monthly" element={<ReportsPage />} />
              <Route path="/reports/realtime" element={<ReportsPage />} />
              <Route path="/reports/apps-media" element={<ReportsPage />} />
              <Route path="/reports/eform-instances" element={<ReportsPage />} />
              <Route path="/reports/eform-pivot" element={<ReportsPage />} />
            <Route path="/reports/commerce" element={<ReportsPage />} />
              <Route path="/reports/billing-system" element={
                <RequirePermission interfaceKey="reports.billingSystem">
                  <ReportsPage />
                </RequirePermission>
              } />
              <Route path="/admin/usage-billing-settings" element={
                <RequirePermission interfaceKey="admin.usageBilling.settings">
                  <UsageBillingSettingsPage />
                </RequirePermission>
              } />
              <Route path="/commerce/collections" element={<CommerceCollectionsPage />} />
              <Route path="/commerce/products" element={<CommerceProductsPage />} />
              <Route path="/commerce/wpa-home-promotions" element={<CommerceWpaHomePromotionPage />} />
              <Route path="/commerce/import" element={<CommerceImportPage />} />
              <Route path="/commerce/import-schedule" element={<CommerceImportSchedulePage />} />
              <Route path="/commerce/catalog-admin" element={<CommerceCatalogAdminPage />} />
              <Route path="/commerce/customers" element={<CommerceCustomersPage />} />
              <Route path="/commerce/price-cards" element={<CommercePriceCardsPage />} />
              <Route path="/commerce/sales-orders" element={<CommerceSalesOrdersPage />} />
              <Route path="/commerce/delivery-rules" element={<CommerceDeliveryRulesPage />} />
              <Route path="/commerce/wpa-permissions" element={<CommerceWpaPermissionPage />} />
              <Route path="/onboarding" element={<OnboardingManagementPage />} />
              <Route path="/onboarding-test" element={<OnboardingTest />} />
              <Route path="/onboarding-wizard/:companyId" element={<OnboardingWizardPage />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Content>
        </Layout>
        <MyPreferencesModal
          visible={showPreferences}
          onClose={() => setShowPreferences(false)}
          userInfo={userInfo}
          onUserInfoUpdate={setUserInfo}
        />
        <OnboardingTrigger />
      </Layout>
    );
  }

  // 若設定了自訂登入路徑，且目前路徑不符，不顯示登入表單（安全隱藏）
  // loginPath === null 代表 localStorage 尚未有快取（第一次訪問且 fetch 尚未完成），
  // 此時視為使用預設路徑 '/'，避免在 fetch 完成前短暫閃爍空白頁。
  const configuredPath = loginPath; // null = first load (treat as '/'), '' = default, '/xxx' = custom
  const isCustomPath = configuredPath !== null && configuredPath !== '';
  if (isCustomPath && location.pathname !== configuredPath) {
    return null;
  }

  const versionLabel = systemVersion
    ? (systemVersion.toLowerCase().startsWith('v') ? systemVersion : `v${systemVersion}`)
    : '';

  return (
    <div className="login-root">
      <div className="login-left">
        <img src="/assets/Whatoflow_logo_W.png" alt="WhatoFlow Logo" className="theme-img" />
      </div>
      <div className="login-right">
        <div className="login-box">
          <img src="/assets/starchy-logo.png" alt="logo" className="logo-img" />
          <h2 className="platform-title">
            {t('login.platformTitlePrefix')}
            <span className="platform-title-highlight">{t('login.platformTitleHighlight')}</span>
            {t('login.platformTitleSuffix')}
          </h2>
          <div className="language-switcher-container" style={{ marginBottom: 16 }}>
            <LanguageSwitcher />
          </div>
          <form className="login-form" onSubmit={e => {
            e.preventDefault();
            onFinish({ email: email, password });
          }}>
            <input
              type="text"
              placeholder={t('login.accountOrEmail') || t('login.email') || '帳號或 Email'}
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="login-input"
              autoFocus
              autoComplete="username"
            />
            <input
              type="password"
              placeholder={t('login.password')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="login-input"
            />
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? t('common.loading') : t('login.loginButton')}
            </button>
          </form>
          {versionLabel && (
            <div className="login-version">{versionLabel}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#7234CF',
        },
      }}
    >
      <LanguageProvider>
        <Router>
          <AppContent />
        </Router>
      </LanguageProvider>
    </ConfigProvider>
  );
}

export default App; 